import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { Transaction } from 'xrpl/dist/npm'
import { getXrplClient } from './client'
import { recordRecentTransaction } from './transactions'
import { getWalletConnector, isGemWalletInstalled, walletConnectors, WalletType } from './wallet'
import { XrplSubmitResult } from './types'

type XrplContextState = {
  account: string | null
  wallet: WalletType | null
  wallets: { id: WalletType; name: string; description: string; available: boolean }[]
  connecting: boolean
  connect: (walletId: WalletType) => Promise<void>
  disconnect: () => void
  signAndSubmit: (tx: Transaction) => Promise<XrplSubmitResult>
}

const XrplContext = createContext<XrplContextState | undefined>(undefined)

const STORAGE_KEY = 'xrpl:account'
const WALLET_KEY = 'xrpl:wallet'

export const XrplProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [account, setAccount] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY))
  const [wallet, setWallet] = useState<WalletType | null>(() => {
    const stored = localStorage.getItem(WALLET_KEY)
    return stored ? (stored as WalletType) : null
  })
  const [connecting, setConnecting] = useState(false)
  const [availability, setAvailability] = useState<Record<WalletType, boolean>>({} as Record<WalletType, boolean>)

  const connect = useCallback(async (walletId: WalletType) => {
    setConnecting(true)
    try {
      const connector = getWalletConnector(walletId)
      if (!connector.isAvailable() && walletId !== 'gemwallet' && walletId !== 'xumm') {
        throw new Error(`${connector.name} is not available`)
      }
      const session = await connector.connect()
      setAccount(session.account)
      setWallet(walletId)
      localStorage.setItem(STORAGE_KEY, session.account)
      localStorage.setItem(WALLET_KEY, walletId)
    } finally {
      setConnecting(false)
    }
  }, [])

  const disconnect = useCallback(() => {
    setAccount(null)
    setWallet(null)
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(WALLET_KEY)
  }, [])

  const signAndSubmit = useCallback(
    async (tx: Transaction) => {
      if (!account || !wallet) {
        throw new Error('Wallet not connected')
      }
      const connector = getWalletConnector(wallet)
      const result = await connector.signAndSubmit(tx)
      if (result?.hash) {
        try {
          recordRecentTransaction(account, result.hash, (tx as any)?.TransactionType)
        } catch (error) {
          // ignore local storage errors
        }
      }
      return result
    },
    [account, wallet],
  )

  useEffect(() => {
    getXrplClient().catch((error) => {
      console.error('Failed to connect XRPL client', error)
    })
  }, [])

  useEffect(() => {
    let isMounted = true
    let retryTimer: number | undefined
    let retryTimerLong: number | undefined
    const checkWallets = async () => {
      try {
        const installed = await isGemWalletInstalled()
        if (isMounted) {
          if (installed) {
            setAvailability((prev) => ({ ...prev, gemwallet: true }))
          }
        }
      } catch (error) {
        // keep default sync detection fallback
      }
    }
    checkWallets()
    if (typeof window !== 'undefined') {
      retryTimer = window.setTimeout(checkWallets, 1000)
      retryTimerLong = window.setTimeout(checkWallets, 3000)
      window.addEventListener('focus', checkWallets)
    }
    return () => {
      isMounted = false
      if (typeof window !== 'undefined') {
        if (retryTimer) {
          window.clearTimeout(retryTimer)
        }
        if (retryTimerLong) {
          window.clearTimeout(retryTimerLong)
        }
        window.removeEventListener('focus', checkWallets)
      }
    }
  }, [])

  const value = useMemo(
    () => ({
      account,
      wallet,
      wallets: walletConnectors.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        available: availability[item.id] ?? item.isAvailable(),
      })),
      connecting,
      connect,
      disconnect,
      signAndSubmit,
    }),
    [account, wallet, connecting, connect, disconnect, signAndSubmit, availability],
  )

  return <XrplContext.Provider value={value}>{children}</XrplContext.Provider>
}

export const useXrpl = () => {
  const context = useContext(XrplContext)
  if (!context) {
    throw new Error('useXrpl must be used within XrplProvider')
  }
  return context
}
